<script>
  import { createEventDispatcher } from "svelte";

  export let serial_number = "";
  export let sidecar_info = {};
  export let spans = [];
  export let maxSpans = 50000;
  export let memoryBytes = 0;
  export let formatBytes;
  export let dropStrategy = "drop_oldest";
  export let tracing_enabled;
  export let confirmClear = false;
  export let showResourceInfo = false;
  export let hasResource = false;
  export let resourceAttrs = {};

  const dispatch = createEventDispatcher();
</script>

<div
  class="shrink-0 border-b border-line-strong bg-surface px-3 py-2 flex items-center justify-between gap-3"
>
  <!-- Left: Title, sidecar link, badges -->
  <div class="flex items-center gap-2 min-w-0">
    <span class="text-sm font-semibold text-content shrink-0">Trace Explorer</span>
    {#if serial_number}
      <span class="text-gray-300 select-none shrink-0">—</span>
      <a
        href={sidecar_info.admin_url || `/admin/sidecars/${serial_number}`}
        class="text-sm font-mono text-blue-600 hover:text-blue-800 hover:underline truncate"
        title="View sidecar admin"
      >
        {serial_number}
      </a>
    {/if}
    {#if sidecar_info.hardware_variant}
      <span
        class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-surface-muted text-content-subtle uppercase tracking-wide shrink-0"
      >
        {sidecar_info.hardware_variant}
      </span>
    {/if}
    {#if sidecar_info.location}
      <span
        class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-surface-muted text-content-subtle shrink-0"
      >
        {sidecar_info.location}
      </span>
    {/if}
    {#if sidecar_info.device_label}
      <span class="text-xs text-content-subtle truncate shrink-0">
        {sidecar_info.device_label}
      </span>
    {/if}
    {#if sidecar_info.platform_name}
      <span class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-50 text-blue-600 shrink-0">
        {sidecar_info.platform_name}
      </span>
    {/if}
  </div>

  <!-- Center: Capacity & memory -->
  <div class="flex items-center gap-3 text-xs text-content-subtle">
    <span class="tabular-nums">
      <span class="font-medium text-content">{spans.length.toLocaleString()}</span>
      <span class="text-content-subtle">/</span>
      <span>{maxSpans.toLocaleString()}</span>
      spans
    </span>
    <span class="text-gray-300 select-none">·</span>
    <span class="tabular-nums">
      <span class="font-medium text-content">{formatBytes(memoryBytes)}</span>
    </span>
    {#if dropStrategy === "drop_newest"}
      <span
        class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-50 text-amber-600"
        title="Newer spans are dropped when capacity is reached"
      >
        keep oldest
      </span>
    {/if}
    <!-- Resource info popover -->
    {#if hasResource}
      <div class="relative">
        <button
          class="w-5 h-5 flex items-center justify-center rounded hover:bg-surface-hover text-content-subtle hover:text-content-muted transition-colors"
          title="Node resource info"
          on:click={() => dispatch("toggleResourceInfo")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            class="w-3.5 h-3.5"
          >
            <path
              fill-rule="evenodd"
              d="M15 8A7 7 0 1 1 1 8a7 7 0 0 1 14 0ZM9 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM6.75 8a.75.75 0 0 0 0 1.5h.75v1.75a.75.75 0 0 0 1.5 0v-2.5A.75.75 0 0 0 8.25 8h-1.5Z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
        {#if showResourceInfo}
          <div
            class="absolute top-full right-0 mt-1 z-40 bg-surface border border-line-strong rounded-lg shadow-lg p-3 min-w-[280px] max-w-[400px]"
          >
            <div class="text-xs font-semibold text-content mb-2">Node Resource Attributes</div>
            {#each Object.entries(resourceAttrs).sort( ([a], [b]) => a.localeCompare(b) ) as [key, value]}
              <div class="flex gap-2 text-xs py-0.5 min-w-0">
                <span class="text-content-subtle shrink-0">{key}:</span>
                <span class="text-content truncate"
                  >{typeof value === "object" ? JSON.stringify(value) : value}</span
                >
              </div>
            {/each}
          </div>
        {/if}
      </div>
    {/if}
  </div>

  <!-- Right: Controls -->
  <div class="flex items-center gap-3 shrink-0">
    <!-- Recording status -->
    {#if tracing_enabled}
      <span class="flex items-center gap-1.5 text-xs text-green-600 font-medium">
        <span class="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0"></span>
        Recording
      </span>
    {:else}
      <span class="flex items-center gap-1.5 text-xs text-content-subtle">
        <span class="w-2 h-2 rounded-full bg-surface-hover shrink-0"></span>
        Stopped
      </span>
    {/if}

    <!-- Clear with inline confirm -->
    {#if spans.length > 0}
      {#if confirmClear}
        <span class="text-xs text-content-muted">Clear all data?</span>
        <button
          class="px-2.5 py-1 text-xs font-medium rounded border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
          on:click={() => dispatch("clearButton")}
        >
          Yes, clear
        </button>
        <button
          class="px-2.5 py-1 text-xs font-medium rounded border border-line-strong text-content-muted hover:bg-surface-hover transition-colors"
          on:click={() => dispatch("cancelClear")}
        >
          Cancel
        </button>
      {:else}
        <button
          class="px-2.5 py-1 text-xs font-medium rounded border border-line-strong text-content-muted hover:bg-surface-hover transition-colors"
          on:click={() => dispatch("clearButton")}
        >
          Clear
        </button>
      {/if}
    {/if}

    <button
      class="px-2.5 py-1 text-xs font-medium rounded border transition-colors {tracing_enabled
        ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
        : 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'}"
      on:click={() => dispatch("toggleTracing")}
    >
      {tracing_enabled ? "Stop Tracing" : "Start Tracing"}
    </button>
  </div>
</div>
